import type { Problem } from './types';

/**
 * Typed error thrown by the axios response interceptor when the server
 * returns an RFC 7807 `application/problem+json` body (or any non-2xx).
 */
export class QflyError extends Error {
  public readonly status: number;
  public readonly type: string;
  public readonly detail?: string;
  public readonly requestId?: string;
  public readonly traceId?: string;
  public readonly fieldErrors: ReadonlyArray<{ readonly field?: string; readonly message: string }>;

  constructor(problem: Problem) {
    super(problem.title || `Request failed (${problem.status})`);
    this.name = 'QflyError';
    this.status = problem.status;
    this.type = problem.type;
    this.detail = problem.detail;
    this.requestId = problem.requestId;
    this.traceId = problem.traceId;
    this.fieldErrors = problem.errors ?? [];
  }
}

/** Narrows an unknown thrown value to {@link QflyError}. */
export const isQflyError = (value: unknown): value is QflyError => value instanceof QflyError;

/** Humanises a snake_case api_name into a Title-Case label for messages. */
const humaniseField = (api: string): string =>
  api
    .split('_')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

/** Parses any value into a {@link Problem} with defensive fallbacks. */
export const toProblem = (raw: unknown, fallbackStatus = 500): Problem => {
  if (raw && typeof raw === 'object') {
    const obj = raw as Partial<Problem> & Record<string, unknown>;

    // Stage-transition-blocked body (F-O-8 required-fields + Dashboard v2
    // motion). This is a deliberate NON-7807 shape — `{error, missing_fields,
    // stage}` — so the generic status+title check below misses it and it would
    // otherwise degrade to "Unexpected error". Recognise it here and map the
    // raw api_names to readable labels so the SPA can surface which fields the
    // stage needs.
    if (obj.error === 'stage_transition_blocked' && Array.isArray(obj.missing_fields)) {
      const fields = (obj.missing_fields as unknown[]).filter(
        (f): f is string => typeof f === 'string',
      );
      const stage = typeof obj.stage === 'string' ? obj.stage : 'this stage';
      const labels = fields.map(humaniseField);
      const title =
        labels.length > 0
          ? `${labels.join(', ')} ${labels.length === 1 ? 'is' : 'are'} required for ${stage}.`
          : `A required field is missing for ${stage}.`;
      return {
        type: 'stage_transition_blocked',
        title,
        status: 422,
        detail: title,
        errors: fields.map((f) => ({
          field: f,
          message: `${humaniseField(f)} is required for ${stage}.`,
        })),
      };
    }

    if (typeof obj.status === 'number' && typeof obj.title === 'string') {
      return {
        type: typeof obj.type === 'string' ? obj.type : 'about:blank',
        title: obj.title,
        status: obj.status,
        detail: typeof obj.detail === 'string' ? obj.detail : undefined,
        instance: typeof obj.instance === 'string' ? obj.instance : undefined,
        requestId: typeof obj.requestId === 'string' ? obj.requestId : undefined,
        traceId: typeof obj.traceId === 'string' ? obj.traceId : undefined,
        errors: Array.isArray(obj.errors)
          ? obj.errors.filter(
              (e): e is { field?: string; message: string } =>
                !!e &&
                typeof e === 'object' &&
                typeof (e as { message?: unknown }).message === 'string',
            )
          : undefined,
      };
    }
  }
  return {
    type: 'about:blank',
    title: 'Unexpected error',
    status: fallbackStatus,
    detail: typeof raw === 'string' ? raw : undefined,
  };
};
